import i18n from "i18n-js";
import { Logger } from "@devforce/trailhead-client-logger";
export const PACKAGE_NAME = "@devforce/trailhead-client-locale";
export const SUPPORTED_LOCALES = [
    "de",
    "en",
    "es",
    "es-MX",
    "fr",
    "it",
    "ja",
    "ko",
    "pt-BR",
    "zh-CN",
];
export const SALESFORCE_LOCALES_MAP = {
    en: "en_US",
    es: "es",
    de: "de",
    fr: "fr",
    it: "it",
    ja: "ja",
    ko: "ko",
    "es-MX": "es_MX",
    "pt-BR": "pt_BR",
    "zh-CN": "zh_CN",
};
export const DEFAULT_LOCALE = "en";
export const DEFAULT_FALLBACKS = false;
export const logger = new Logger({
    prefix: `[${PACKAGE_NAME}]: `,
});
i18n.fallbacks = DEFAULT_FALLBACKS;
i18n.missingTranslation = (key) => {
    let message = `missing "${key}" translation for locale "${i18n.locale}"`;
    logger.warn(message);
    return `[${message}]`;
};
i18n.missingPlaceholder = (placeholder, label) => {
    logger.warn(`missing "${placeholder}" placeholder in "${label}"`);
    return `[missing ${placeholder} value]`;
};
export class I18n {
    constructor(scope) {
        this.id = getUniqueId();
        if (scope)
            this.addScope(scope);
    }
    t(scope, key, options) {
        return i18n.t(`${scope}_${this.id}.${key}`, options);
    }
    addScope(scope) {
        for (const [scopeName, locales] of Object.entries(scope)) {
            for (const [locale, labels] of Object.entries(locales)) {
                const localeKey = toLocaleKey(locale);
                i18n.translations[localeKey] = {
                    ...i18n.translations[localeKey],
                    [`${scopeName}_${this.id}`]: labels,
                };
            }
        }
    }
}
/**
 * Normalized the provided locale one that Trailhead supports
 */
export function toLocaleKey(value) {
    var _a, _b, _c;
    // Parse out language and locale
    const regex = value.match(/(?<lang>[a-z]{2,3})(?:[_-])?(?<locale>[A-Z]{2})?/);
    // Create a valid tag
    // https://www.loc.gov/standards/iso639-2/php/English_list.php
    // https://datatracker.ietf.org/doc/html/rfc5646
    const tag = [(_a = regex === null || regex === void 0 ? void 0 : regex.groups) === null || _a === void 0 ? void 0 : _a.lang, (_c = (_b = regex === null || regex === void 0 ? void 0 : regex.groups) === null || _b === void 0 ? void 0 : _b.locale) === null || _c === void 0 ? void 0 : _c.toUpperCase()]
        .filter(Boolean)
        .join("-") || value.toLowerCase();
    // Find if value is lowercase
    if (value.includes("-") && value.split("-").length == 2) {
        const match = SUPPORTED_LOCALES.find((l) => l.toLowerCase() === value.toLowerCase());
        if (match)
            return match;
    }
    // Find an exact match
    const exact = SUPPORTED_LOCALES.find((l) => l === tag);
    if (exact) {
        return exact;
    }
    // Fallback to a supported language (en-US => en)
    const fallback = SUPPORTED_LOCALES.find((l) => new RegExp(`^${l}`).test(tag));
    if (fallback) {
        return fallback;
    }
    // Give up
    logger.warn(`Locale ${value} not supported!`);
    return value;
}
export function configure(configHandler) {
    state.configureHandlers.push(configHandler);
    for (let i18n of state.i18n) {
        configHandler(i18n);
    }
}
export function setLocale(locale) {
    const normalized = toLocaleKey(locale);
    configure((i18n) => {
        i18n.locale = normalized;
    });
    for (let handler of state.changeHandlers) {
        handler(normalized);
    }
}
export function getLocale() {
    return i18n.currentLocale();
}
export function getLanguage() {
    return toLocaleKey(i18n.currentLocale()).split("-")[0];
}
export function registerLocaleChangeHandler(handler) {
    state.changeHandlers.push(handler);
    return () => {
        state.changeHandlers = state.changeHandlers.filter((h) => h !== handler);
    };
}
export function localizeNumber(input) {
    const n = typeof input === "string" ? parseFloat(input.replace(/,/, "")) : input;
    return n.toLocaleString(getLocale());
}
export function localizeDate(input, options = {}) {
    const n = new Date(input);
    return n.toLocaleDateString(getLocale(), options);
}
export function getUniqueId() {
    return (state.previousId += 1);
}
let state = {
    configureHandlers: [],
    changeHandlers: [],
    i18n: [i18n],
    // This is arbitrarily high to avoid conflicting with old versions before the guids were unique
    previousId: 2000,
};
export function initializeState(currentState, existingState) {
    if (!existingState)
        return currentState;
    // Most likely, i18n is the same between modules as the i18n-js module attaches to window and then exports any
    // previously set instance. To ensure backwards compatibility, and to guard against changes to the i18n-js module in
    // the future, we keep the array of instances here.
    if (!existingState.i18n.includes(i18n)) {
        existingState.i18n = [...existingState.i18n, i18n];
    }
    // Run configurations that happened before this module was loaded
    for (let configHandler of existingState.configureHandlers) {
        configHandler(i18n);
    }
    // Ensure the previousId doesn't match between runs
    existingState.previousId += 1;
    return existingState;
}
// This module may be executed multiple times from different sources
if (typeof window !== "undefined") {
    // @ts-ignore
    window[PACKAGE_NAME] = state = initializeState(state, window[PACKAGE_NAME]);
}
